import { useCallback } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import type { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { appInsightsService } from "@/services/appInsightsService";

/**
 * Properties object for telemetry events
 */
type TelemetryProperties = Record<string, string | number | boolean>;

/**
 * Measurements object for telemetry events
 */
type TelemetryMeasurements = Record<string, number>;

/**
 * Return type for useApplicationInsights hook
 */
interface IUseApplicationInsightsReturn {
  appInsights: ReactPlugin | null;
  trackEvent: (name: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements) => void;
  trackPageView: (name?: string, uri?: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements) => void;
  trackException: (exception: Error, severityLevel?: number, properties?: TelemetryProperties, measurements?: TelemetryMeasurements) => void;
  trackMetric: (name: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: TelemetryProperties) => void;
  isInitialized: boolean;
}

/**
 * Custom hook to access Application Insights functionality
 * Provides easy access to telemetry tracking methods
 */
export function useApplicationInsights(): IUseApplicationInsightsReturn {
  const appInsights = useAppInsightsContext();

  const trackEvent = useCallback((name: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void => {
    appInsightsService.trackEvent(name, properties, measurements);
  }, []);

  const trackPageView = useCallback((name?: string, uri?: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void => {
    appInsightsService.trackPageView(name, uri, properties, measurements);
  }, []);

  const trackException = useCallback((exception: Error, severityLevel?: number, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void => {
    appInsightsService.trackException(exception, severityLevel, properties, measurements);
  }, []);

  const trackMetric = useCallback((name: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: TelemetryProperties): void => {
    appInsightsService.trackMetric(name, average, sampleCount, min, max, properties);
  }, []);

  return {
    appInsights,
    trackEvent,
    trackPageView,
    trackException,
    trackMetric,
    isInitialized: !!appInsights,
  };
}